import { LitElement, css, html } from 'lit'
import './ct-term-card.js'

import {
  getProgramByKey,
} from '../data/programs/index.js'

import {
  getAvailability,
} from '../utils/get-availability.js'

import {
  getPilotById,
} from '../data/pilots.js'

import {
  getTripsByIds,
} from '../api/vakanza-api.js'

export class CtOfferTerms extends LitElement {
  static properties = {
    programKey: {
      type: String,
      attribute: 'program-key',
    },

    terms: {
      state: true,
    },

    loading: {
      state: true,
    },

    errorMessage: {
      state: true,
    },
  }

  constructor() {
    super()

    this.programKey = ''
    this.terms = []
    this.loading = false
    this.errorMessage = ''
  }

  static styles = css`
    :host {
      display: block;
      width: 100%;
    }

    .terms {
      display: grid;
      grid-auto-flow: column;
      gap: 20px;
      width: 100%;
      overflow-x: auto;
      padding-bottom: 14px;
      scroll-snap-type: x proximity;
      scrollbar-width: thin;
    }

    .terms--single {
      grid-auto-columns: min(100%, 420px);
    }

    .terms--double {
      grid-auto-columns: min(
        calc((100% - 20px) / 2),
        420px
      );
    }

    .terms--multiple {
      grid-auto-columns:
        calc((100% - 40px) / 3);
    }

    ct-term-card {
      display: block;
      width: 100%;
      min-width: 0;
      scroll-snap-align: start;
    }

    .message {
      margin: 0;
      padding: 20px;
      border-radius: 8px;
      background: #f5f5f5;
      color: #666666;
      font-family: Poppins, Arial, sans-serif;
      text-align: center;
    }

    .message--error {
      color: #a12a2a;
      background: #fff1f1;
    }

    @media (max-width: 900px) {
      .terms {
        grid-auto-columns:
          calc((100% - 20px) / 2);
      }
    }

    @media (max-width: 640px) {
      .terms {
        grid-auto-columns: 100%;
        gap: 14px;
      }
    }
  `

  updated(changedProperties) {
    if (changedProperties.has('programKey')) {
      this.loadTerms()
    }
  }

  async loadTerms() {
    const program =
      getProgramByKey(this.programKey)

    if (!program) {
      this.terms = []
      this.loading = false
      this.errorMessage =
        'Nie znaleziono terminów dla tego programu.'

      return
    }

    this.loading = true
    this.errorMessage = ''

    try {
      const result = await getTripsByIds(
        program.tripIds
      )

      if (result.missing.length > 0) {
        console.info(
          'Pominięto niewidoczne terminy:',
          result.missing
        )
      }

      if (result.errors.length > 0) {
        console.warn(
          'Nie udało się pobrać części terminów:',
          result.errors
        )
      }

      this.terms = result.data
        .map(trip => {
          const manualDetails =
            program.termDetails?.[
              String(trip.id)
            ] ?? {}

          return {
            ...trip,
            ...manualDetails,
          }
        })
        .sort((firstTrip, secondTrip) => {
          return String(
            firstTrip.startDate
          ).localeCompare(
            String(secondTrip.startDate)
          )
        })

      if (
        this.terms.length === 0 &&
        result.errors.length > 0
      ) {
        this.errorMessage =
          'Nie udało się pobrać terminów. Odśwież stronę i spróbuj ponownie.'
      }
    } catch (error) {
      console.error(
        'Błąd pobierania terminów:',
        error
      )

      this.terms = []
      this.errorMessage =
        'Nie udało się pobrać dostępnych terminów.'
    } finally {
      this.loading = false
    }
  }

  render() {
    if (this.loading) {
      return html`
        <p class="message">
          Pobieranie terminów...
        </p>
      `
    }

    if (this.errorMessage) {
      return html`
        <p class="message message--error">
          ${this.errorMessage}
        </p>
      `
    }

    if (this.terms.length === 0) {
      return html`
        <p class="message">
          Obecnie nie ma dostępnych terminów.
        </p>
      `
    }

    return html`
      <section
        class="terms terms--${getTermsLayout(
          this.terms.length
        )}"
        aria-label="Dostępne terminy wyjazdu"
      >
        ${this.terms.map(term => {
          const availability =
            getAvailability(term)

          const pilot =
            getPilotById(term.pilotId)

          const hasBookingUrl =
            typeof term.reservationUrl ===
              'string' &&
            term.reservationUrl.length > 0

          const canBook =
            availability.bookingEnabled &&
            hasBookingUrl

          const bookingLabel =
            availability.variant === 'sold-out'
              ? 'Brak miejsc'
              : hasBookingUrl
                ? 'Sprawdź dostępność'
                : 'Rezerwacja niedostępna'

                const currentPrice =
  Number(term.price?.amount)

const shouldShowPriceNote =
  Number.isFinite(currentPrice) &&
  currentPrice === Number(term.priceNotePrice)

          return html`
            <ct-term-card
              .date=${term.displayDate ?? ''}
              .group=${term.group ?? ''}
              .airport=${term.airport ?? ''}
              .status=${availability.label}
              .statusVariant=${availability.variant}
              .pilotName=${pilot?.name ?? ''}
              .pilotImage=${pilot?.image ?? ''}
              .price=${formatCurrentPrice(
                term.price
              )}
              .flightPrice=${term.flightPrice ?? ''}
              .priceNote=${shouldShowPriceNote
  ? term.priceNote ?? ''
  : ''}
              .bookingEnabled=${canBook}
              .bookingLabel=${bookingLabel}
              .bookingUrl=${term.reservationUrl ?? ''}
            ></ct-term-card>
          `
        })}
      </section>
    `
  }
}

function formatCurrentPrice(price) {
  const amount = Number(price?.amount)

  if (!Number.isFinite(amount)) {
    return ''
  }

  const formattedAmount =
    new Intl.NumberFormat('pl-PL', {
      maximumFractionDigits: 0,
    }).format(amount)

  const currency =
    price?.currency === 'PLN'
      ? 'zł'
      : price?.currency ?? ''

  return `${formattedAmount} ${currency} / os.`
}

function getTermsLayout(termsCount) {
  if (termsCount === 1) {
    return 'single'
  }

  if (termsCount === 2) {
    return 'double'
  }

  return 'multiple'
}

if (!customElements.get('ct-offer-terms')) {
  customElements.define(
    'ct-offer-terms',
    CtOfferTerms
  )
}